import { useEffect } from 'react';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import { Box, Button, Container, Divider, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { QRCodeSVG } from 'qrcode.react';
import galenaLogo from '@/assets/images/logos/galena.svg';
import galenaBadge from '@/assets/images/svg/certificate-badge-galena.svg';
import defaultBadge from '@/assets/images/svg/certificate-badge.svg';
import type { Certificate } from '@/data/certificates/interfaces';
import { useSetTitle } from '@/hooks/use-set-title';
import { CopyToClipboardWrapper } from '@/packages/certificates/components/copy-to-clipboard';
import { formatDuration } from '@/utils/masks/time';

const PRINT_STYLE_ID = 'certificate-print-style';

const injectPrintStyles = () => {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.innerHTML = `
    @media print {
      @page {
        margin: 10mm;
        size: landscape;
      }
    }
  `;
  document.head.appendChild(style);
};

const removePrintStyles = () => {
  const style = document.getElementById(PRINT_STYLE_ID);
  if (style) style.remove();
};

const buildLinkedInShareText = (courseName: string, certificateUrl: string) =>
  `Mais um curso concluído na Eduplay: ${courseName}. Que venham os próximos desafios! 🚀\n\n${certificateUrl}`;

// LinkedIn's official share dialog (sharing/share-offsite) only accepts a `url` and
// ignores any text param, so it can't prefill the post. The feed composer deep link
// isn't officially documented, but it does prefill the post body via `text`.
const buildLinkedInShareUrl = (shareText: string) =>
  `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;

const CurrentDateAndTime = () => {
  const now = new Date();

  return (
    <>{`${Intl.DateTimeFormat('pt-BR').format(now)} às ${now.toLocaleTimeString('pt-BR', { hour12: false })}`}</>
  );
};

export const CertificateViewer = ({
  certificate,
}: {
  certificate: Certificate;
}) => {
  const {
    course_duration,
    course_ended_at,
    course_name,
    customization,
    institution_name,
    user_name,
  } = certificate;
  useSetTitle(`Certificado ${course_name} - ${institution_name}`);
  const { palette } = useTheme();
  const primaryColor = customization?.primary_color || palette.primary.dark;
  const secondaryColor = customization?.secondary_color || palette.info.light;
  const badgeImage = customization ? defaultBadge : galenaBadge;
  const customLogo = customization?.logo_url;
  const certificateLogo = customLogo ?? galenaLogo;
  const certificateLogoWidth = customLogo ? 150 : 88;
  const certificateUrl = window.location.href;
  const linkedInShareText = buildLinkedInShareText(course_name, certificateUrl);

  useEffect(() => {
    const handleBeforePrint = () => injectPrintStyles();
    const handleAfterPrint = () => removePrintStyles();

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      removePrintStyles();
    };
  }, []);

  return (
    <Container
      sx={{
        '&&': {
          '@media print': {
            margin: '0',
            padding: '0',
          },
        },
      }}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Box className="media-print-remove" py={3}>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => window.print()}
              startIcon={<SaveAltRoundedIcon />}
              variant="outlined"
            >
              Salvar PDF
            </Button>
            <CopyToClipboardWrapper content={certificateUrl}>
              <Button startIcon={<LinkRoundedIcon />} variant="outlined">
                Copiar link
              </Button>
            </CopyToClipboardWrapper>
            <CopyToClipboardWrapper content={linkedInShareText}>
              <Button
                onClick={() =>
                  window.open(
                    buildLinkedInShareUrl(linkedInShareText),
                    '_blank',
                    'noopener,noreferrer,width=600,height=600'
                  )
                }
                startIcon={<LinkedInIcon />}
                variant="outlined"
              >
                Compartilhar no LinkedIn
              </Button>
            </CopyToClipboardWrapper>
          </Stack>
        </Box>
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            padding: { xs: 1, md: 3 },

            '& *': { fontFamily: 'Carbona' },

            '@media print': {
              border: '1px solid #ccc',
              padding: 3,
              margin: '0 auto',
            },
          })}
        >
          <Box
            sx={() => ({
              borderColor: secondaryColor,
              borderWidth: { xs: '6px', md: '12px' },
              borderStyle: 'solid',
              padding: { xs: 3, md: 7 },
              pt: { xs: 6, md: 7 },

              '@media print': {
                borderWidth: '12px',
                p: 7,
              },

              '&&': {
                pb: 3,
              },
            })}
          >
            <Stack spacing={{ xs: 6, md: '72px' }}>
              <Box
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                display="flex"
                justifyContent="space-between"
                sx={{
                  flexDirection: { xs: 'column', md: 'row' },

                  '@media print': {
                    flexDirection: 'row',
                  },
                }}
                gap={1}
              >
                <Box
                  sx={() => ({
                    color: primaryColor,
                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  })}
                >
                  Certificado
                </Box>
                <Box
                  sx={{
                    flex: '1',
                    maxWidth: certificateLogoWidth,
                  }}
                >
                  <Box
                    component="img"
                    src={certificateLogo}
                    sx={{ width: '100%' }}
                    title="Logotipo"
                  />
                </Box>
              </Box>
              <Box>
                <Box
                  sx={(theme) => ({
                    color: primaryColor,
                    fontSize: { xs: 28, md: 36 },
                    fontWeight: theme.typography.fontWeightSemibold,
                  })}
                >
                  {certificate.course_name}
                </Box>
              </Box>
              <Box>
                <Box
                  fontWeight="regular"
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: '36px',
                  }}
                >
                  Certificamos que <strong>{user_name}</strong> concluiu o curso{' '}
                  {course_name} oferecido por {institution_name}, com duração de{' '}
                  {formatDuration(
                    course_duration.type,
                    course_duration.quantity
                  )}
                  , em{' '}
                  {new Date(course_ended_at).toLocaleDateString('pt-BR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  .
                </Box>
              </Box>
              {!!customization?.signature_url && (
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <Box
                    component="img"
                    src={customization.signature_url}
                    title="Assinatura"
                    sx={{
                      flex: '1 0 240px',
                      maxWidth: 300,
                      width: '100%',
                    }}
                  />
                </Box>
              )}
              <Stack spacing={3}>
                <Divider
                  sx={(theme) => ({ borderColor: theme.palette.neutral[300] })}
                />
                <Box
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr 1fr',
                      md: 'auto 1fr auto',
                    },
                    gridTemplateRows: {
                      xs: 'auto auto',
                      md: 'auto',
                    },
                    gap: theme.spacing(3),
                    alignItems: 'center',

                    '@media print': {
                      gridTemplateColumns: 'auto 1fr auto',
                      gridTemplateRows: 'auto',
                    },
                  })}
                >
                  <Box
                    sx={{
                      gridColumn: 1,
                      gridRow: 1,
                      justifySelf: 'start',
                    }}
                  >
                    {!!badgeImage && (
                      <Box
                        sx={{
                          backgroundColor: primaryColor,
                          height: 60,
                          WebkitMaskImage: `url(${badgeImage})`,
                          WebkitMaskPosition: 'center',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskSize: 'contain',
                          width: 60,
                          printColorAdjust: 'exact',
                          WebkitPrintColorAdjust: 'exact',
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      gridColumn: { xs: '2', md: '3' },
                      gridRow: { xs: '1', md: '1' },
                      justifySelf: 'end',

                      '@media print': {
                        gridColumn: 3,
                        gridRow: 1,
                      },
                    }}
                  >
                    <Box
                      component={QRCodeSVG}
                      value={certificateUrl}
                      size={80}
                    />
                  </Box>
                  <Box
                    sx={{
                      gridColumn: { xs: '1 / span 2', md: '2' },
                      gridRow: { xs: '2', md: '1' },

                      '@media print': {
                        gridColumn: 2,
                        gridRow: 1,
                      },
                    }}
                  >
                    <Box
                      sx={() => ({
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: '20px',
                        wordBreak: 'break-word',
                      })}
                    >
                      Este certificado foi emitido em {<CurrentDateAndTime />}{' '}
                      pela plataforma Galena e pode ser verificado através do QR
                      Code ou acessando{' '}
                      <a href={certificateUrl} target="_blank">
                        {certificateUrl}{' '}
                      </a>
                      .
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
